// ============================================
// AlpenScreen - Pricing Page (COMPLETE WITH NAV + FOOTER)
// File: frontend/src/pages/Pricing.jsx
// Purpose: Public/authenticated pricing page - Full AlpenScreen Design
// ============================================

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthAndSubscription();
  }, []);

  const checkAuthAndSubscription = async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      setIsAuthenticated(true);
      
      try {
        const response = await fetch(`${API_URL}/subscription/details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCurrentSubscription(data.subscription);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      }
    }
  };

  const handleSelectPlan = async (tier, priceId) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    if (tier === 'enterprise') {
      alert(t('pricingPage.enterprise.contactMessage'));
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          priceId: priceId,
          tier: tier,
          organizationId: currentSubscription?.organizationId
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        alert(t('pricingPage.errors.checkoutFailed'));
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(t('pricingPage.errors.checkoutError'));
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Re-create tiers array when language changes
  const tiers = useMemo(() => [
    {
      id: 'starter',
      name: t('pricingPage.tiers.starter.name'),
      price: 29,
      screens: 5,
      trialDays: 14,
      badge: t('pricingPage.tiers.starter.badge'),
      badgeColor: 'bg-blue-100 text-blue-700',
      features: [
        t('pricingPage.tiers.starter.features.screens'),
        t('pricingPage.tiers.starter.features.weather'),
        t('pricingPage.tiers.starter.features.clock'),
        t('pricingPage.tiers.starter.features.media'),
        t('pricingPage.tiers.starter.features.playlists'),
        t('pricingPage.tiers.starter.features.multilang'),
        t('pricingPage.tiers.starter.features.trial')
      ],
      addons: [
        { 
          name: t('pricingPage.tiers.starter.addons.rss'), 
          price: 12 
        }
      ],
      priceId: process.env.REACT_APP_STRIPE_PRICE_STARTER,
      buttonClass: 'bg-blue-500 hover:bg-blue-600',
      current: currentSubscription?.tier === 'starter'
    },
    {
      id: 'business',
      name: t('pricingPage.tiers.business.name'),
      price: 99,
      screens: 15,
      trialDays: 14,
      popular: true,
      badge: t('pricingPage.tiers.business.badge'),
      badgeColor: 'bg-blue-600 text-white',
      features: [
        t('pricingPage.tiers.business.features.screens'),
        t('pricingPage.tiers.business.features.weather'),
        t('pricingPage.tiers.business.features.clock'),
        t('pricingPage.tiers.business.features.media'),
        t('pricingPage.tiers.business.features.playlists'),
        t('pricingPage.tiers.business.features.multilang'),
        t('pricingPage.tiers.business.features.groups'),
        t('pricingPage.tiers.business.features.trial')
      ],
      addons: [
        { 
          name: t('pricingPage.tiers.business.addons.rss'), 
          price: 12 
        }
      ],
      priceId: process.env.REACT_APP_STRIPE_PRICE_BUSINESS,
      buttonClass: 'bg-blue-600 hover:bg-blue-700',
      current: currentSubscription?.tier === 'business'
    },
    {
      id: 'enterprise',
      name: t('pricingPage.tiers.enterprise.name'),
      price: 299,
      screens: null,
      comingSoon: true,
      badge: t('pricingPage.tiers.enterprise.badge'),
      badgeColor: 'bg-gray-200 text-gray-700',
      features: [
        t('pricingPage.tiers.enterprise.features.unlimited'),
        t('pricingPage.tiers.enterprise.features.allWidgets'),
        t('pricingPage.tiers.enterprise.features.rssIncluded'),
        t('pricingPage.tiers.enterprise.features.priority'),
        t('pricingPage.tiers.enterprise.features.custom'),
        t('pricingPage.tiers.enterprise.features.manager'),
        t('pricingPage.tiers.enterprise.features.sla'),
        t('pricingPage.tiers.enterprise.features.onpremise')
      ],
      buttonClass: 'bg-gray-300 text-gray-600',
      current: currentSubscription?.tier === 'enterprise'
    }
  ], [t, i18n.language, currentSubscription]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation - LIKE LANDING PAGE */}
      <nav className="fixed top-0 left-0 right-0 bg-white/98 backdrop-blur-sm border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div 
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img 
                src="/assets/logo/alpenscreen-horizontal-transparent.svg" 
                alt="AlpenScreen" 
                className="h-10"
              />
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-6">
              <a 
                href={`/?lang=${i18n.language}#features`}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                {t('landing.nav.features')}
              </a>
              <a 
                href={`/?lang=${i18n.language}#pricing`}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                {t('landing.nav.pricing')}
              </a>
              
              <LanguageSwitcher style="landing" />
              
              {isAuthenticated ? (
                <button 
                  onClick={() => navigate('/dashboard')} 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button 
                    onClick={() => navigate('/login')} 
                    className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
                  >
                    {t('landing.nav.login')}
                  </button>
                  <button 
                    onClick={() => navigate('/register')} 
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    {t('landing.nav.freeTrial')}
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSwitcher style="landing" />
              {isAuthenticated ? (
                <button 
                  onClick={() => navigate('/dashboard')} 
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium"
                >
                  Dashboard
                </button>
              ) : (
                <button 
                  onClick={() => navigate('/login')} 
                  className="px-3 py-1.5 text-gray-700 text-sm font-medium"
                >
                  {t('landing.nav.login')}
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content - with top padding for fixed nav */}
      <div className="pt-20 pb-16 bg-gray-50">
        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('pricingPage.header.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('pricingPage.header.subtitle')}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {tiers.map((tier) => {
              const isPopular = tier.popular && !tier.current;
              
              return (
                <div
                  key={tier.id}
                  className={`
                    relative rounded-2xl p-8 transition-all duration-300
                    ${tier.current 
                      ? 'border-2 border-blue-500 bg-blue-50 shadow-xl' 
                      : isPopular
                        ? 'border-3 border-blue-600 bg-white shadow-2xl md:scale-105'
                        : 'border-2 border-gray-200 bg-white hover:shadow-lg'
                    }
                  `}
                >
                  {/* Badge */}
                  <div className="flex justify-center mb-6">
                    <span className={`
                      ${tier.badgeColor} 
                      text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide
                    `}>
                      {tier.badge}
                    </span>
                  </div>

                  {/* Current Plan Indicator */}
                  {tier.current && (
                    <div className="absolute -top-3 right-8">
                      <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        ✓ {t('pricingPage.labels.currentPlan')}
                      </span>
                    </div>
                  )}

                  {/* Name */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {tier.name}
                    </h3>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-gray-600 text-lg">CHF</span>
                      <span className="text-5xl font-bold text-blue-600">
                        {tier.price}
                      </span>
                      <span className="text-gray-600">
                        {t('pricingPage.labels.perMonth')}
                      </span>
                    </div>
                    {tier.trialDays && (
                      <p className="text-sm text-gray-600 mt-2">
                        {t('pricingPage.labels.trialDays', { days: tier.trialDays })}
                      </p>
                    )}
                    {tier.comingSoon && (
                      <p className="text-sm text-gray-600 mt-2">
                        {t('pricingPage.labels.consultation')}
                      </p>
                    )}
                  </div>

                  {/* Screens */}
                  <div className="text-center mb-6 pb-6 border-b border-gray-200">
                    <p className="text-lg font-semibold text-gray-900">
                      {tier.screens 
                        ? t('pricingPage.labels.screensCount', { count: tier.screens })
                        : t('pricingPage.labels.unlimitedScreens')
                      }
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Add-Ons */}
                  {tier.addons && tier.addons.length > 0 && (
                    <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        {t('pricingPage.labels.optional')}:
                      </p>
                      {tier.addons.map((addon, index) => (
                        <p key={index} className="text-xs text-gray-600">
                          + {addon.name} (CHF {addon.price}/mo)
                        </p>
                      ))}
                    </div>
                  )}

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(tier.id, tier.priceId)}
                    disabled={tier.current || tier.comingSoon || loading}
                    className={`
                      w-full py-4 rounded-xl font-bold text-lg transition-all duration-200
                      ${tier.current || tier.comingSoon
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                        : `${tier.buttonClass} text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5`
                      }
                    `}
                  >
                    {tier.current 
                      ? t('pricingPage.buttons.currentPlan')
                      : tier.comingSoon
                        ? t('pricingPage.buttons.comingSoon')
                        : loading
                          ? t('pricingPage.buttons.loading')
                          : t('pricingPage.buttons.choosePlan', { plan: tier.name })
                    }
                  </button>
                </div>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              {t('pricingPage.allPlansInclude.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <div className="text-4xl mb-3">🇨🇭</div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {t('pricingPage.allPlansInclude.swissMade.title')}
                </h4>
                <p className="text-sm text-gray-600">
                  {t('pricingPage.allPlansInclude.swissMade.description')}
                </p>
              </div>
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <div className="text-4xl mb-3">🌍</div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {t('pricingPage.allPlansInclude.multiLanguage.title')}
                </h4>
                <p className="text-sm text-gray-600">
                  {t('pricingPage.allPlansInclude.multiLanguage.description')}
                </p>
              </div>
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <div className="text-4xl mb-3">📱</div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {t('pricingPage.allPlansInclude.mobileOptimized.title')}
                </h4>
                <p className="text-sm text-gray-600">
                  {t('pricingPage.allPlansInclude.mobileOptimized.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - LIKE LANDING PAGE */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Footer Content */}
          <div className="text-center mb-8">
            <div className="flex flex-col items-center gap-4 mb-6">
              <img 
                src="/assets/logo/alpenscreen-icon-transparent.svg" 
                alt="AlpenScreen" 
                className="h-12 w-auto cursor-pointer"
                onClick={scrollToTop}
              />
              <p className="text-white text-xl font-bold tracking-wide">
                ALPENSCREEN
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center items-center gap-4 text-sm">
              <a href={`/?lang=${i18n.language}#features`} className="hover:text-white transition-colors">
                {t('landing.footer.features')}
              </a>
              <span className="text-gray-600">•</span>
              <a href={`/?lang=${i18n.language}#pricing`} className="hover:text-white transition-colors">
                {t('landing.footer.pricing')}
              </a>
              <span className="text-gray-600">•</span>
              <button 
                onClick={() => navigate('/register')} 
                className="hover:text-white transition-colors"
              >
                {t('landing.footer.freeTrial')}
              </button>
              <span className="text-gray-600">•</span>
              <button 
                onClick={() => navigate('/support')} 
                className="hover:text-white transition-colors"
              >
                {t('landing.footer.support')}
              </button>
              <span className="text-gray-600">•</span>
              <button 
                onClick={() => navigate('/privacy')} 
                className="hover:text-white transition-colors"
              >
                {t('landing.footer.privacy')}
              </button>
              <span className="text-gray-600">•</span>
              <button 
                onClick={() => navigate('/terms')} 
                className="hover:text-white transition-colors"
              >
                {t('landing.footer.terms')}
              </button>
              <span className="text-gray-600">•</span>
              <button 
                onClick={() => navigate('/imprint')} 
                className="hover:text-white transition-colors"
              >
                {t('landing.footer.imprint')}
              </button>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 border-t border-gray-700 text-center">
            <p className="text-gray-500 text-sm">
              {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;